// Reusable photo picker — gives the user a clear choice between camera and
// photo library. Drop in anywhere photos are needed (job photos, before/after,
// equipment, receipts, signed agreements scanned in, etc.).
//
// Usage:
//   <PhotoPicker onPicked={(file) => uploadFile(file)} label="Add Photo"/>
//
// Behavior:
//   - Tap button → mobile-friendly bottom-sheet choice modal
//   - "📷 Take Photo" opens device camera (rear-facing default)
//   - "🖼️ Choose from Library" opens photo library / file picker
//   - "Cancel" closes the sheet
// On desktop both options just open a normal file dialog (no camera available).

import React, { useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner } from '../ui.jsx';

export function PhotoPicker({ onPicked, label = '📷 Add Photo', upload = false, multiple = false, accept = 'image/*', toast }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const trigger = (capture) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    if (capture) input.capture = capture; // 'environment' = rear cam, 'user' = front
    input.multiple = !!multiple;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) { setOpen(false); return; }
      setOpen(false);
      if (!upload) {
        onPicked?.(multiple ? files : files[0]);
        return;
      }
      // Auto-upload + return URL(s) instead of raw file
      setBusy(true);
      try {
        const uploaded = [];
        for (const f of files) {
          if (f.size > 10 * 1024 * 1024) { toast?.(`${f.name} too big (10MB max)`, 'error'); continue; }
          const base64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(f);
          });
          const r = await api('/api/uploads', { method:'POST', body:{ name: f.name, mime: f.type, base64 }});
          uploaded.push({ url: r.url, name: f.name, mime: f.type, size: f.size });
        }
        onPicked?.(multiple ? uploaded : uploaded[0]);
      } catch(err) { toast?.(err.message, 'error'); } finally { setBusy(false); }
    };
    input.click();
  };

  return <>
    <Btn sz="sm" v="primary" onClick={()=>setOpen(true)} disabled={busy}>
      {busy ? <Spinner sz={14}/> : label}
    </Btn>

    {open && <>
      <div onClick={()=>setOpen(false)} style={{
        position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:9500,
      }}/>
      <div role="dialog" aria-label="Add a photo" style={{
        position:'fixed', left:0, right:0, bottom:0, zIndex:9501,
        background:G.card, borderTop:`1px solid ${G.border}`,
        borderRadius:'18px 18px 0 0',
        padding:'18px 18px calc(18px + env(safe-area-inset-bottom, 0px))',
        boxShadow:'0 -8px 32px rgba(0,0,0,.5)',
        maxWidth:520, margin:'0 auto',
        animation:'slideUp .18s ease-out',
      }}>
        <div style={{ width:36, height:4, background:G.border, borderRadius:2, margin:'0 auto 14px' }}/>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:14, textAlign:'center' }}>Add a photo</div>
        <button onClick={()=>trigger('environment')} style={{
          width:'100%', padding:'18px 16px', marginBottom:10,
          background:G.input, border:`1px solid ${G.cardBorder}`, borderRadius:12,
          display:'flex', alignItems:'center', gap:14, cursor:'pointer',
          color:G.text, fontSize:16, fontWeight:600, textAlign:'left',
        }}>
          <span style={{ fontSize:28 }}>📷</span>
          <div>
            <div>Take Photo</div>
            <div style={{ fontSize:12, color:G.textMuted, fontWeight:400 }}>Use your device camera</div>
          </div>
        </button>
        <button onClick={()=>trigger(null)} style={{
          width:'100%', padding:'18px 16px', marginBottom:10,
          background:G.input, border:`1px solid ${G.cardBorder}`, borderRadius:12,
          display:'flex', alignItems:'center', gap:14, cursor:'pointer',
          color:G.text, fontSize:16, fontWeight:600, textAlign:'left',
        }}>
          <span style={{ fontSize:28 }}>🖼️</span>
          <div>
            <div>Choose from Library</div>
            <div style={{ fontSize:12, color:G.textMuted, fontWeight:400 }}>Pick existing photo{multiple ? 's' : ''}</div>
          </div>
        </button>
        <button onClick={()=>setOpen(false)} style={{
          width:'100%', padding:'14px 16px',
          background:'transparent', border:'none', cursor:'pointer',
          color:G.textMuted, fontSize:15, fontWeight:600,
        }}>Cancel</button>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </>}
  </>;
}
